import api, { queryClient } from "@app/config/api/api";
import type { ChatHistoryMessage, ExperimentId } from "@globals";
import type { ChatMessage } from "@lib/agent/types";
import { notifications } from "@lib/notification/notification";
import { useQuery } from "@tanstack/react-query";

export const agentSessionMessagesOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/agent-session/messages", {
    params: { path: { experiment_id } },
    queryKey: ["agent-session", experiment_id, "messages"]
  });

export const useAgentSessionMessages = (experiment_id: ExperimentId) =>
  useQuery({
    ...agentSessionMessagesOptions(experiment_id),
    enabled: Boolean(experiment_id),
    // Default `throwOnError: true` from queryClient would route 403 (feature flag) and 5xx
    // into the error boundary; we render an inline error state in the chat area instead.
    throwOnError: false
  });

export const useClearAgentSession = () =>
  api.useMutation("delete", "/experiments/{experiment_id}/agent-session", {
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: agentSessionMessagesOptions(vars.params.path.experiment_id).queryKey
      });
    },
    onError: error => {
      notifications.error("Failed to clear conversation", error.message);
    }
  });

export const useSubmitSessionFeedback = () => api.useMutation("post", "/agent-proxy/feedback/");

type ContentBlock = { text?: string };

function flattenContent(content: ChatHistoryMessage["content"]): string {
  if (!Array.isArray(content)) {
    return "";
  }
  return (content as ContentBlock[]).map(block => block?.text ?? "").join("");
}

export function translateBackendMessages(messages: ChatHistoryMessage[]): ChatMessage[] {
  // Strands persists every turn, including `role: "user"` messages whose content is purely
  // `toolResult` blocks and assistant turns that are only `toolUse`. Those have no text and
  // would render as empty chat bubbles, so drop them — tool activity is already conveyed via
  // the streaming `agent.tool_use` path on the live chat side.
  return messages.flatMap(m => {
    const content = flattenContent(m.content);
    if (!content) {
      return [];
    }
    return [
      {
        id: `hist-${m.message_index}`,
        role: m.role === "assistant" ? "assistant" : "user",
        content
      }
    ];
  });
}
